import { ArrowRight } from 'lucide-react';
import { useState } from 'react';

import { useGetStudentById } from '~entities/student';

import { Button } from '~shared/shadcn/ui/button';
import { Card } from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';

interface StudentsClassPageProps {
  studentId: string;
}

interface Policy {
  name: string;
  description: string;
}

export const StudentPoliciesPage = ({ studentId }: StudentsClassPageProps) => {
  const { data } = useGetStudentById(studentId);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  const toPolicy = (policy: { name: string; description: string } | string) =>
    typeof policy === 'string'
      ? { name: policy, description: '' }
      : { name: policy.name, description: policy.description };

  const policyGroupToPolicies = (policyGroup: unknown) =>
    Array.isArray(policyGroup) ? policyGroup.map(toPolicy) : [];

  // Extract policies from both class enrollments and event enrollments
  const allPolicies = [
    ...(data?.enrollments?.flatMap((enrollment) =>
      policyGroupToPolicies(enrollment.enrollmentId?.policyGroup),
    ) ?? []),
    ...(data?.events?.flatMap((event) =>
      policyGroupToPolicies(event.eventId?.policyGroup),
    ) ?? []),
  ];

  // Dedupe policies: duplicate enrollment rows (or shared policies across
  // classes) would otherwise render the same policy card multiple times.
  const policies = allPolicies
    ? Array.from(
        new Map(
          allPolicies.map((policy) => [
            `${policy.name}\u0000${policy.description}`,
            policy,
          ]),
        ).values(),
      )
    : allPolicies;

  return (
    <div className='px-2 pb-40 sm:px-0'>
      <div className='mt-4 flex w-full min-w-0 flex-wrap gap-x-10 gap-y-4'>
        {policies?.map(({ name, description }) => (
          <Dialog key={`${name}\u0000${description}`}>
            <DialogTrigger asChild>
              <Card
                className='flex w-full min-w-0 flex-1 items-center justify-between gap-3 rounded-md p-4 shadow-md transition-shadow duration-200 hover:shadow-lg sm:min-w-[27rem]'
                onClick={() => {
                  setSelectedPolicy({ name, description });
                }}
              >
                <div className='min-w-0'>
                  <div className='break-words font-medium'>{name}</div>
                </div>
                <Button
                  className='h-fit shrink-0 rounded-full p-1'
                  variant='secondary'
                >
                  <ArrowRight size={12} strokeWidth={3} />
                </Button>
              </Card>
            </DialogTrigger>

            <DialogContent className='w-[min(24rem,calc(100%-4rem))] max-w-none rounded-lg p-6'>
              <DialogClose asChild>
                <button
                  className='absolute right-4 top-4 text-gray-600 transition-colors hover:text-gray-800'
                  onClick={() => {
                    setSelectedPolicy(null);
                  }}
                ></button>
              </DialogClose>
              <DialogTitle className='text-lg font-semibold'>
                {selectedPolicy?.name}
              </DialogTitle>
              <DialogDescription className='mt-2 max-h-60 overflow-y-auto text-sm text-muted-foreground'>
                {selectedPolicy?.description
                  .split('\n')
                  .map((line: string, index: number) => (
                    <p key={index} className='mb-2'>
                      {line === '' ? <br /> : line}
                    </p>
                  ))}
              </DialogDescription>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </div>
  );
};
